import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useRole } from "../../contexts/RoleContext";
import { useCompany } from "../../contexts/CompanyContext";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";

const pageTitles: Record<string, { title: string; subtitle: string }> = {
  "/dashboard":      { title: "대시보드",          subtitle: "오늘의 현황" },
  "/inbox":          { title: "인박스",             subtitle: "AI 거래처와 메일" },
  "/approval":       { title: "전자결재",           subtitle: "계약서 · 기안서 · 예산안" },
  "/ar-hub":         { title: "A&R 크리에이티브 허브", subtitle: "아티스트 · 데모 · 앨범 기획" },
  "/finance":        { title: "재무 / ERP",         subtitle: "예산 · 계좌 · 세금 관리" },
  "/marketing":      { title: "마케팅 워룸",        subtitle: "SNS · 차트 · 보도자료" },
  "/legal":          { title: "법무 & IP 관리",     subtitle: "계약서 검토 · 저작권 · 법적 대응" },
  "/concert-hub":    { title: "공연기획 허브",      subtitle: "라인업 · 티켓 · 스폰서" },
  "/foundation-hub": { title: "사업관리 허브",      subtitle: "지원사업 · 보조금 · 후원자" },
  "/ip-hub":         { title: "IP 스튜디오",        subtitle: "연재 · 라이선싱 · 저작권" },
  "/campaign-hub":   { title: "캠페인 허브",        subtitle: "광고 · 영상제작 · 성과분석" },
};

// 알림 타입별 메타
const NOTIF_META: Record<string, { icon: string; label: string; route?: string; color: string }> = {
  budget_request:    { icon: "ri-money-dollar-circle-line", label: "예산 요청",  route: "/approval",  color: "text-[#7A9BFF]" },
  approval_request:  { icon: "ri-file-list-3-line",        label: "결재 요청",  route: "/approval",  color: "text-[#7A9BFF]" },
  contract_review:   { icon: "ri-file-paper-2-line",       label: "계약 검토",  route: "/approval",  color: "text-[#7A9BFF]" },
  final_sign:        { icon: "ri-pen-nib-line",            label: "최종 서명",  route: "/approval",  color: "text-[#CCCCCC]" },
  admin_event:       { icon: "ri-flashlight-line",         label: "강사 이벤트", route: "/dashboard", color: "text-[#CCCCCC]" },
  b2b_request:       { icon: "ri-exchange-line",           label: "B2B 요청",   route: "/inbox",     color: "text-[#888888]" },
};

interface TopBarProps {
  onEventClick?: () => void;
  hasNewEvent?: boolean;
}

export default function TopBar({ onEventClick, hasNewEvent }: TopBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAllRead, roleConfig } = useRole();
  const { workspaceId } = useCompany();
  const { jobRole } = useRole();
  const { profile } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [collabUnread, setCollabUnread] = useState(0);

  const pageInfo = pageTitles[location.pathname] ?? { title: "K-ArtPEN", subtitle: "" };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // ── 협업 메일 미읽음 수 실시간 구독 ─────────────────────
  useEffect(() => {
    if (!workspaceId || !jobRole) return;

    const fetchCount = async () => {
      const { count } = await supabase
        .from("collab_messages")
        .select("*", { count: "exact", head: true })
        .eq("workspace_id", workspaceId)
        .eq("receiver_role", jobRole)
        .eq("is_read", false);
      setCollabUnread(count ?? 0);
    };

    fetchCount();

    const channel = supabase
      .channel(`collab-unread-${workspaceId}-${jobRole}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "collab_messages", filter: `workspace_id=eq.${workspaceId}` },
        () => { fetchCount(); }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [workspaceId, jobRole]);

  const timeStr = currentTime.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
  const totalUnread = unreadCount + collabUnread;

  const myNotifications = notifications
    .filter(n => n.to_role === roleConfig.key || n.to_role === "ALL")
    .slice(0, 10);

  const handleNotifClick = (n: typeof myNotifications[0]) => {
    const meta = NOTIF_META[n.type];
    if (meta?.route) {
      setShowNotifPanel(false);
      if (meta.route === "/approval" && n.ref_id) {
        navigate(`/approval?ref=${n.ref_id}`);
      } else {
        navigate(meta.route);
      }
    }
  };

  const handleBellClick = () => {
    setShowNotifPanel(!showNotifPanel);
    if (!showNotifPanel) markAllRead();
  };

  return (
    <header className="h-12 bg-white/95 backdrop-blur border-b border-black/[0.08] flex items-center px-5 gap-4 sticky top-0 z-30">
      {/* Page Title */}
      <div className="flex-1 flex items-baseline gap-2">
        <h1 className="text-[#111111] font-medium text-sm leading-tight">{pageInfo.title}</h1>
        {pageInfo.subtitle && (
          <span className="text-[#BBBBBB] text-[11px] hidden md:block">— {pageInfo.subtitle}</span>
        )}
      </div>

      {/* Emergency Event Alert */}
      {hasNewEvent && (
        <button
          type="button"
          onClick={onEventClick}
          className="flex items-center gap-1.5 bg-black/[0.04] border border-black/[0.08] text-[#555555] text-xs px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap hover:bg-black/[0.07] transition-colors"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#4B6CF7]/60 animate-pulse inline-block"></span>
          긴급 이벤트
        </button>
      )}

      {/* Time */}
      <div className="text-[#BBBBBB] text-xs tabular-nums hidden md:block">{timeStr}</div>

      {/* Profile Avatar */}
      <button
        type="button"
        onClick={() => navigate("/profile")}
        title="내 프로필"
        className="w-7 h-7 flex items-center justify-center rounded-full overflow-hidden border border-black/[0.1] hover:border-[#4B6CF7]/40 transition-colors cursor-pointer shrink-0"
      >
        {profile?.avatar_url ? (
          <img src={profile.avatar_url} alt="프로필" className="w-full h-full object-cover object-top" />
        ) : (
          <div className="w-full h-full bg-[#F0F2F8] flex items-center justify-center">
            <i className="ri-user-3-line text-[#AAAAAA] text-xs"></i>
          </div>
        )}
      </button>

      {/* Notification Bell */}
      <div className="relative">
        <button
          type="button"
          onClick={handleBellClick}
          className="relative w-7 h-7 flex items-center justify-center text-[#BBBBBB] hover:text-[#555555] rounded-lg transition-colors cursor-pointer"
        >
          <i className="ri-notification-3-line text-base"></i>
          {totalUnread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 flex items-center justify-center bg-[#4B6CF7] rounded-full border border-white text-white text-[8px] font-bold px-0.5 leading-none">
              {totalUnread > 9 ? "9+" : totalUnread}
            </span>
          )}
        </button>

        {showNotifPanel && (
          <div className="absolute right-0 top-9 w-80 bg-white border border-black/[0.08] rounded-xl z-50 overflow-hidden shadow-xl">
            <div className="px-4 py-3 border-b border-black/[0.07] flex items-center justify-between">
              <span className="text-[#111111] text-xs font-semibold">알림</span>
              {totalUnread > 0 && (
                <span className="text-[#999999] text-[10px]">{totalUnread}개 읽지 않음</span>
              )}
            </div>

            {/* ── 협업 메일 미읽음 알림 항목 ── */}
            {collabUnread > 0 && (
              <div
                className="px-4 py-3 border-b border-black/[0.05] hover:bg-[#4B6CF7]/[0.03] cursor-pointer transition-colors"
                onClick={() => { navigate("/inbox"); setShowNotifPanel(false); }}
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-emerald-100 shrink-0 mt-0.5">
                    <i className="ri-mail-send-line text-emerald-500 text-xs"></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className="text-[9px] text-emerald-600 border border-emerald-200 bg-emerald-50 px-1 py-0.5 rounded shrink-0">
                        협업 메일
                      </span>
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                    </div>
                    <p className="text-[#333333] text-xs font-medium">
                      미읽음 협업 요청 {collabUnread}건
                    </p>
                    <p className="text-[#999999] text-[10px] mt-0.5">인박스 → 협업 메일 탭에서 확인하세요</p>
                  </div>
                </div>
                <div className="mt-1.5 ml-8">
                  <span className="text-emerald-500/80 text-[10px] flex items-center gap-1">
                    <i className="ri-arrow-right-s-line"></i>협업 메일함으로 이동
                  </span>
                </div>
              </div>
            )}

            {myNotifications.length === 0 && collabUnread === 0 ? (
              <div className="px-4 py-8 text-center text-[#BBBBBB] text-xs">새로운 알림이 없습니다</div>
            ) : (
              <div className="max-h-72 overflow-y-auto">
                {myNotifications.map((n) => {
                  const meta = NOTIF_META[n.type];
                  const isActionable = !!meta?.route;
                  return (
                    <div
                      key={n.id}
                      onClick={() => handleNotifClick(n)}
                      className={`px-4 py-3 border-b border-black/[0.05] transition-colors ${
                        isActionable ? "hover:bg-black/[0.02] cursor-pointer" : "cursor-default"
                      }`}
                    >
                      <div className="flex items-start gap-2.5">
                        <div className="w-6 h-6 flex items-center justify-center rounded-lg bg-black/[0.04] shrink-0 mt-0.5">
                          <i className={`${meta?.icon ?? "ri-notification-3-line"} text-xs ${meta?.color ?? "text-[#555555]"}`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            {meta && (
                              <span className="text-[9px] text-[#999999] border border-black/[0.08] px-1 py-0.5 rounded shrink-0">
                                {meta.label}
                              </span>
                            )}
                            <div className={`w-1.5 h-1.5 rounded-full shrink-0 ${n.is_read ? "bg-black/[0.08]" : "bg-[#4B6CF7]/60"}`} />
                          </div>
                          <p className="text-[#333333] text-xs leading-snug font-medium truncate">{n.title}</p>
                          <p className="text-[#999999] text-[10px] mt-0.5 leading-relaxed line-clamp-2">{n.body}</p>
                        </div>
                      </div>
                      {isActionable && meta?.route === "/approval" && (
                        <div className="mt-2 ml-8">
                          <span className="text-[#4B6CF7]/70 text-[10px] flex items-center gap-1">
                            <i className="ri-arrow-right-s-line"></i>결재 문서 확인
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {(myNotifications.length > 0 || collabUnread > 0) && (
              <div className="px-4 py-2 border-t border-black/[0.06]">
                <span className="text-[#CCCCCC] text-[10px]">클릭하면 관련 페이지로 이동합니다</span>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
